import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { clearListFilters } from '@lib/useListFilters';

export interface TokenStoreContextValue {
  currentToken: string | null;
  storeToken: (token: string | null, ownerType: string) => void;
  clearToken: () => void;
  setUserType: (type: string) => void;
  isReady: boolean;
}

const noop = () => {
  console.warn('Calling TokenStore method outside TokenStoreProvider scope.');
};

const TokenStoreContext = createContext<TokenStoreContextValue>({
  currentToken: null,
  storeToken: noop,
  clearToken: noop,
  setUserType: noop,
  isReady: false,
});

const accessTokenSuffix = 'access_token';

export const useTokenStore = (): TokenStoreContextValue => {
  return useContext(TokenStoreContext);
};

export const TokenStore = ({ children }: { children: React.ReactNode }) => {
  const [currentToken, setCurrentToken] = useState<string | null>(null);
  const [userType, setUserType] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);

  const getTokenKey = useCallback((type: string) => `${type}_${accessTokenSuffix}`, []);

  useEffect(() => {
    if (!userType) return;

    const token = sessionStorage.getItem(getTokenKey(userType));
    setCurrentToken(token);
    setIsReady(true);
  }, [userType, getTokenKey]);

  const storeToken = useCallback(
    (token: string | null, ownerType: string) => {
      const generatedKey = getTokenKey(ownerType);
      if (!token) {
        sessionStorage.removeItem(generatedKey);
        return;
      }
      sessionStorage.setItem(generatedKey, token);
      setCurrentToken(token);
    },
    [getTokenKey]
  );

  const clearToken = useCallback(() => {
    if (!userType) return;
    sessionStorage.removeItem(getTokenKey(userType));
    clearListFilters();
  }, [userType, getTokenKey]);

  return (
    <TokenStoreContext.Provider value={{ currentToken, storeToken, clearToken, setUserType, isReady }}>
      {children}
    </TokenStoreContext.Provider>
  );
};
